/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
'use client';

import { useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import { default as Image } from '@/components/global/images/resized-cdn-image';
import { useParams, useRouter } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { StrapiCarrier } from '@/apis/strapi';
import { ReservationDetails, ScheduleProduct, ScheduleRun } from '@/apis/tds';
import { doTranslation } from '@/app/i18n/client';
import ButtonSmall from '@/components/global/buttons/button-small';
import ButtonTertiary from '@/components/global/buttons/button-tertiary';
import Button from '@/components/global/buttons/button';
import DelayedDateSelector from '@/components/global/date-selector/delayed-date-selector';
import Select from '@/components/global/forms/select';
import Modal from '@/components/global/modal/modal';
import Carrier from '@/components/schedule-results/carrier';
import { CarrierImage } from '@/utils/carriers';
import { formatTime24Hour } from '@/utils/formatters/dateFormatter';
import { formatDuration, formatDurationSROnly } from '@/utils/formatters/durationFormatter';
import { formatDateToYearMonthDay, formatReadableDate } from '@/utils/formatters/dateFormatter';

import styles from '@/components/schedule-results/schedule-cards.module.scss';

import {
  determineWhichFareToShow,
  formatDateString,
  formatDate,
  formatPrice,
  formatTime,
  getCarrier,
  getFares,
  getRescheduleFares,
  isScheduleBookable,
  getCarriers
} from '@/utils/schedules';

import dynamic from 'next/dynamic';

// Lazy load components with loading placeholders
const ScheduleTripDetails = dynamic(() => import('@/components/schedule-results/schedule-trip-details'), {
  ssr: false,
  loading: () => <div className={styles.loading}>Loading trips...</div>
});

const ScheduleUpgradeTrip = dynamic(() => import('@/components/schedule-results/schedule-upgrade-trip'), {
  ssr: false,
  loading: () => <div className={styles.loading}>Loading upgrades...</div>
});

const ScheduleFareTypeSelection = dynamic(() => import('@/components/schedule-results/schedule-fare-type-selection'), {
  ssr: false,
  loading: () => <div className={styles.loading}>Loading fares...</div>
});

import { StrapiCityDatum } from '@/apis/strapi';
import { pickRandomFAQParts } from '@/utils/faqs';

type Props = {
  arriveTimeSelected?: string | null;
  departTimeSelected?: string | null;
  carrierImages: { [key: string]: CarrierImage };
  carrierInfo: { [key: string]: StrapiCarrier };
  earliestSchedule: ScheduleProduct | null;
  fareSelected?: string | null;
  handleOrderBy?: (column: string, type: string) => void;
  handleSelect?:
    | ((
        id: string,
        originUuid: string,
        destinationUuid: string,
        fare: string,
        departTime: string,
        arriveTime: string,
        trueNumber: string
      ) => void)
    | null;
  isPurchaseSchedules?: boolean;
  isReschedule?: boolean;
  isSchedules?: boolean;
  isSelected?: boolean;
  lowestPrice: number | null;
  navigate?: (type: string, uuid: string, originUuid: string, destinationUuid: string) => Promise<void>;
  navigateFromSchedules?: (
    type: string,
    uuid: string,
    originUuid: string,
    destinationUuid: string,
    trip: string,
    tripType: string
  ) => Promise<void>;
  rescheduleLeg?: string;
  reservationDetails?: ReservationDetails | null;
  returnDate?: Date | null;
  schedules: ScheduleProduct[];
  scheduleUuidSelected?: string | null;
  setShowingAllTrips?: React.Dispatch<React.SetStateAction<boolean>>;
  shortestTravelTime: number | null;
  trip?: string;
  type: string;
  trailwaysWebstoreAppURL?: string;
  originCityData: StrapiCityDatum | null;
  destinationCityData: StrapiCityDatum | null;
  travelDate: Date | null;
  errorMessage?: string | null;
};

type TripDetails = {
  schedule: ScheduleProduct;
};

type FareType = {
  schedule: ScheduleProduct;
};

type UpgradeTrip = {
  scheduleRun: ScheduleRun;
};

const StationDetails = ({ cityData, stopUuid }: { cityData: StrapiCityDatum | null; stopUuid: string }) => {
  const data = useMemo(() => {
    if (cityData && cityData.attributes.stops) {
      const stop = cityData?.attributes.stops.data.find((stop) => {
        if (
          stop.attributes.partner_location_keys &&
          stop.attributes.partner_location_keys.data &&
          stop.attributes.partner_location_keys.data.length
        ) {
          return stop.attributes.partner_location_keys.data.some(
            (key) => key.attributes.partner_primary_key === stopUuid
          );
        }
        return false;
      });
      return stop?.attributes;
    }
  }, [cityData, stopUuid]);

  return (
    <div className={styles.cities}>
      <span className={styles.line}>{data?.stop_name}</span>
      <span className={styles.line}>{data?.stop_address1}</span>
      <div className={styles.line}>
        {data?.cities?.data && data?.cities?.data.length > 0 && (
          <>
            {data?.cities?.data[0].attributes.city_name},{' '}
            <span className="sr-only">{data?.cities?.data[0].attributes.state?.data.attributes.state_name}</span>
            <span aria-hidden="true">{data?.cities?.data[0].attributes.state?.data.attributes.state_id}</span>{' '}
          </>
        )}
        {data?.stop_postal_code}
      </div>
    </div>
  );
};

const ScheduleCards = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const { push } = useRouter();
  const ref = useRef<Array<HTMLDivElement | null>>([]);
  const [fareType, setFareType] = useState<FareType | null>(null);
  const [fareTypeSelected, setFareTypeSelected] = useState<string | null>(null);
  const [fareTypeId, setFareTypeId] = useState<string>('');
  const [tripDetails, setTripDetails] = useState<TripDetails | null>(null);
  const [tripDetailsId, setTripDetailsId] = useState<string>('');
  const [upgradeTripId, setUpgradeTripId] = useState<string>('');
  const [upgradeTrip, setUpgradeTrip] = useState<UpgradeTrip | null>(null);
  const [scheduleRunDetails, setScheduleRunDetails] = useState<UpgradeTrip | null>(null);
  const [showLoadMore, setShowLoadMore] = useState(props.schedules ? props.schedules.length > 7 : false);
  const [modal, setModal] = useState<boolean>(false);
  const [isProcessingSelection, setIsProcessingSelection] = useState(false);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const isTab = useMediaQuery('(max-width: 1279px)');

  const [interactionType, setInteractionType] = useState('mouse');

  useEffect(() => {
    const handleGlobalKeyDown = () => {
      setInteractionType('keyboard');
    };

    const handleGlobalMouseDown = () => {
      setInteractionType('mouse');
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    document.addEventListener('mousedown', handleGlobalMouseDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      document.removeEventListener('mousedown', handleGlobalMouseDown);
    };
  }, []);

  useEffect(() => {
    const handleFocusAndClick = (event: any) => {
      if (activeIndex != null && !ref.current.some((card) => card && card.contains(event.target)) && !modal) {
        setActiveIndex(null);
        setModal(false);
      }
    };
    document?.addEventListener('focus', handleFocusAndClick, true);
    document?.addEventListener('click', handleFocusAndClick, true);
    return () => {
      document?.removeEventListener('focus', handleFocusAndClick, true);
      document?.removeEventListener('click', handleFocusAndClick, true);
    };
  }, [activeIndex, modal]);

  useEffect(() => {
    const focusEventListner = (event: any) => {
      const currentCard = ref.current.filter((card) => card && card.contains(event.target)) || [];
      if (event.target.getAttribute('data-card-index') != null) {
        setActiveIndex(null);
      } else if (currentCard.length && event.target != currentCard[0]) {
        const currentIndex = currentCard[0]?.getAttribute('data-card-index');
        const parsedIndex = currentIndex ? parseInt(currentIndex, 10) : null;
        if (parsedIndex !== null && parsedIndex !== activeIndex) {
          setActiveIndex(parsedIndex);
        }
      }
    };
    document?.addEventListener('focus', focusEventListner, true);
    return () => {
      document?.removeEventListener('focus', focusEventListner, true);
    };
  }, [activeIndex]);

  useEffect(() => {
    if (!showLoadMore && props.schedules.length > 7) {
      ref.current[7]?.focus();
      if (props.isPurchaseSchedules && props.setShowingAllTrips) {
        props.setShowingAllTrips(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoadMore]);

  useEffect(() => {
    setShowLoadMore(props.schedules.length > 7);
  }, [props.schedules]);

  const { t } = doTranslation(lng);
  const orderByOptions = [
    { label: t('Departure Time'), value: 'departure time' },
    { label: t('Saver Price'), value: 'saver' },
    { label: t('Flex Price'), value: 'flex' },
    {
      label: t('Price'),
      value: 'price'
    },
    { label: t('Duration'), value: 'duration' },
    { label: t('Transfers'), value: 'transfers' }
  ];

  const buildTabs = (schedule: ScheduleProduct) => {
    const tabs: { key: string; label: string }[] = [];
    if (isLowestFare(schedule)) {
      tabs.push({ key: 'cheapest', label: t('Cheapest') });
    }
    if (schedule.scheduleRun.departTime === props?.earliestSchedule?.scheduleRun.departTime) {
      tabs.push({ key: 'earliest', label: t('Earliest') });
    }
    if (schedule.scheduleRun.travelDuration === props.shortestTravelTime) {
      tabs.push({ key: 'fastest', label: t('Fastest') });
    }
    if (['cheapest', 'fastest'].every((key) => tabs.some((tab) => tab.key === key))) {
      tabs.push({ key: 'recommended', label: t('Recommended') });
    }
    if (schedule.scheduleRun.soldOut) {
      tabs.push({ key: 'soldout', label: t('Sold Out') });
    }
    return tabs;
  };

  const transferNumberText = (schedule: ScheduleProduct) => {
    const transfers = schedule.segments.filter((segment: any) => segment.transfer).length;
    if (transfers) {
      const transferWord = transfers !== 1 ? t('transfers') : t('transfer');
      return `${transfers} ${transferWord}`;
    }
    return '0 ' + t('transfers');
  };

  const getScheduleFares = (schedule: ScheduleProduct) => {
    if (props.isReschedule && props.reservationDetails && props.rescheduleLeg) {
      return getRescheduleFares(schedule, props.reservationDetails, props.rescheduleLeg);
    }
    return getFares(schedule);
  };

  const isLowestFare = (schedule: ScheduleProduct) => {
    if (props.trip === 'return' && props.fareSelected === 'flex') {
      const lowestPrice = Math.min(
        ...props.schedules.map((s: ScheduleProduct) => {
          const fares = getScheduleFares(s);
          return fares.flex[0]?.amount || 0;
        })
      );
      const fares = getScheduleFares(schedule);
      return fares.flex[0]?.amount === lowestPrice;
    }
    const fare = determineWhichFareToShow(schedule);
    return fare?.amount === props.lowestPrice;
  };

  const buildTitle = () => {
    if (props.trip === 'outbound') {
      if (props.isSelected) {
        return t('Departure Schedule');
      }
    }
    if (props.trip === 'return') {
      return t('Choose Return Schedule');
    }
    return t('Choose Departure Schedule');
  };

  const buildSubTitle = () => {
    if (!props.schedules[0]) {
      return '';
    }
    const label = props.isSelected ? t('Schedule for') : t('Schedules from');
    const { scheduleRun } = props.schedules[0];
    const date = formatDateString(scheduleRun.departTime, scheduleRun.origin.timeZone);
    return (
      <>
        {label}{' '}
        <span className="sr-only">
          <time dateTime={formatDateToYearMonthDay(date)}>{formatReadableDate(date, lng)}</time>
        </span>
        <time aria-hidden="true" dateTime={formatDateToYearMonthDay(date)}>
          {date}
        </time>
      </>
    );
  };

  const handleSelect = (
    id: string,
    originUuid: string,
    destinationUuid: string,
    fare: string,
    departTime: string,
    arriveTime: string,
    trueNumber: string
  ) => {
    if (props.handleSelect && props.trip === 'outbound') {
      props.handleSelect(id, originUuid, destinationUuid, fare, departTime, arriveTime, trueNumber);
    }
  };

  const isUnavailable = (amount: number | undefined, fare: string) => {
    if (props.isReschedule) {
      return formatPrice(amount) === 'N/A';
    }
    if (props.trip === 'outbound' && props.isSelected) {
      return props.fareSelected !== fare || formatPrice(amount) === 'N/A';
    }
    if (props.trip === 'return') {
      return props.fareSelected !== fare || formatPrice(amount) === 'N/A';
    }
    return formatPrice(amount) === 'N/A';
  };

  const onBuySaverFare = (amount: number | undefined) => {
    if (scheduleRunDetails !== null) {
      if (isUnavailable(amount, 'flex') || props.isReschedule) {
        handleOnSelect(scheduleRunDetails.scheduleRun, 'saver');
      } else {
        setUpgradeTrip(scheduleRunDetails);
      }
    }
  };

  const onBuyFlexFare = () => {
    if (scheduleRunDetails !== null) {
      handleOnSelect(scheduleRunDetails.scheduleRun, 'flex');
    }
  };

  const onCloseFareTypeSelection = () => {
    setModal(false);
    setFareType(null);
    setFareTypeSelected(null);
    document.getElementById(fareTypeId)?.focus();
  };

  const onCloseTripDetails = () => {
    setModal(false);
    setTripDetails(null);
    document.getElementById(tripDetailsId)?.focus();
  };

  const onCloseUpgradeTrip = () => {
    setModal(false);
    setUpgradeTrip(null);
    document.getElementById(upgradeTripId)?.focus();
  };

  const handleOnSelect = async (scheduleRun: UpgradeTrip['scheduleRun'], fareType: string) => {
    if (props.isPurchaseSchedules && props.navigate) {
      props.navigate(fareType, scheduleRun.scheduleUuid, scheduleRun.origin.stopUuid, scheduleRun.destination.stopUuid);
      return;
    }
    if (props.isSchedules && props.navigateFromSchedules) {
      if (scheduleRun && props.trip === 'outbound' && props.type === 'one way') {
        props.navigateFromSchedules(
          fareType,
          scheduleRun.scheduleUuid,
          scheduleRun.origin.stopUuid,
          scheduleRun.destination.stopUuid,
          props.trip,
          props.type
        );
      } else if (scheduleRun && props.trip === 'outbound' && props.type === 'round trip') {
        handleSelect(
          scheduleRun.scheduleUuid,
          scheduleRun.origin.stopUuid,
          scheduleRun.destination.stopUuid,
          fareType,
          scheduleRun.departTime,
          scheduleRun.arriveTime,
          scheduleRun.trueNumber
        );
        props.navigateFromSchedules(
          fareType,
          scheduleRun.scheduleUuid,
          scheduleRun.origin.stopUuid,
          scheduleRun.destination.stopUuid,
          props.trip,
          props.type
        );
      } else if (scheduleRun && props.trip === 'return' && props.type === 'round trip') {
        props.navigateFromSchedules(
          fareType,
          scheduleRun.scheduleUuid,
          scheduleRun.origin.stopUuid,
          scheduleRun.destination.stopUuid,
          props.trip,
          props.type
        );
      }
    }
  };

  const buildURL = (scheduleRun: ScheduleRun, fareType: string) => {
    const u = new URL(`${props.trailwaysWebstoreAppURL}/purchase/from-trailways-website`);
    const urlParams = new URLSearchParams();
    if (props.trip === 'outbound' && props.type === 'one way') {
      urlParams.set('booking-from', `${scheduleRun.origin.stopUuid}`);
      urlParams.set('booking-to', `${scheduleRun.destination.stopUuid}`);
      urlParams.set('fare-type', fareType);
      urlParams.set('arrive-time', scheduleRun.arriveTime);
      urlParams.set('depart-time', scheduleRun.departTime);
      urlParams.set('schedule-uuid', scheduleRun.scheduleUuid);
    } else if (props.trip === 'return' && props.type === 'round trip') {
      urlParams.set('booking-from', `${scheduleRun.destination.stopUuid}`);
      urlParams.set('booking-to', `${scheduleRun.origin.stopUuid}`);
      urlParams.set('arrive-time', props.arriveTimeSelected ?? '');
      urlParams.set('depart-time', props.departTimeSelected ?? '');
      urlParams.set('schedule-uuid', props.scheduleUuidSelected ?? '');
      urlParams.set('fare-type', fareType);
      urlParams.set('return-schedule-uuid', scheduleRun.scheduleUuid);
      urlParams.set('return-depart-time', scheduleRun.departTime);
      urlParams.set('return-arrive-time', scheduleRun.arriveTime);
    }
    return `${u.toString()}?${urlParams.toString()}`;
  };

  const getActiveButton = (element: HTMLElement) => {
    const buttons = element.querySelectorAll('.selectable-fare');
    if (buttons.length == 2) {
      if (buttons[0].classList.toString().includes('disabled') && buttons[1]?.matches(':focus')) {
        return true;
      } else if (buttons[0]?.matches(':focus') && buttons[1].classList.toString().includes('disabled')) {
        return true;
      } else if (!buttons[0]?.matches(':focus') && buttons[1]?.matches(':focus')) {
        return true;
      }
      return false;
    }
    return buttons[0]?.matches(':focus');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    const activeButton = getActiveButton(event.currentTarget);
    switch (event.key) {
      case 'Tab':
        if (activeIndex !== null) {
          if (!event.shiftKey && activeButton) {
            setActiveIndex(null);
            return;
          }
        }
        break;
      case 'Enter':
      case ' ':
        if (activeIndex === null) {
          setActiveIndex(index);
          return;
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (activeIndex !== null) {
          event.preventDefault();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.section}>
      {!props.isPurchaseSchedules && (
        <div className={styles.block}>
          <div className={styles['title-and-dates']}>
            <div className={styles.heading}>
              <h2 className={styles.title}>{buildTitle()}</h2>
              <span className={styles.subtitle}>{buildSubTitle()}</span>
            </div>
            <a className="visually-hidden" href="#different-depart-date">
              {t('Click here if you need to change the date')}
            </a>
            {!(props.isSelected && props.trip === 'outbound') && (
              <div className={styles['date-selector']}>
                <DelayedDateSelector
                  isSchedules={true}
                  passThroughKey={new Date().getTime().toString()}
                  lng={lng}
                  trip={props.trip}
                />
              </div>
            )}
          </div>
          <div className={styles['order-by']}>
            <label className={styles['order-by-label']} htmlFor="order_by">
              {t('Order by')}
            </label>
            <div className={styles['order-by-dropdown']}>
              <Select
                defaultValue={orderByOptions[0]}
                inputId={`${props.trip}_order_by`}
                instanceId={`${props.trip}_order_by`}
                isSearchable={false}
                mandatory
                name={`${props.trip}_order_by`}
                onChange={(selectedOption: any, actionMeta: any) => {
                  if (props.handleOrderBy) {
                    props.handleOrderBy(selectedOption.value, props.trip ?? 'outbound');
                  }
                  if (
                    interactionType === 'keyboard' &&
                    actionMeta.action === 'select-option' &&
                    actionMeta.name === `${props.trip}_order_by`
                  ) {
                    if (!isProcessingSelection) {
                      setIsProcessingSelection(true);
                      try {
                        const optionId = `react-select-${props.trip}_order_by-option-${orderByOptions.findIndex(
                          (option) => option.value === selectedOption.value
                        )}`;
                        const optionElement = document.getElementById(optionId);
                        if (optionElement) {
                          const clickEvent = new MouseEvent('click', {
                            bubbles: false, // Don't bubble up
                            cancelable: true,
                            view: window
                          });
                          optionElement.dispatchEvent(clickEvent);
                        }
                      } finally {
                        setTimeout(() => {
                          setIsProcessingSelection(false);
                        }, 100);
                      }
                    }
                  }
                }}
                options={orderByOptions}
                placeholder={t('Order by')}
              />
            </div>
          </div>
        </div>
      )}
      <div className={styles.schedules}>
        {props.schedules.length == 0 && props.trip === 'outbound' && (
          <>
            {props.errorMessage && (
              <>
                <meta httpEquiv="Status" content="503 Service Unavailable" />
                <p>{props.errorMessage}</p>
              </>
            )}
            {!props.errorMessage && (
              <>
                <p>
                  {t("We're sorry, there are currently no scheduled trips between")}{' '}
                  <span className={styles.cityName}>
                    {props.originCityData?.attributes.city_name},{' '}
                    {props.originCityData?.attributes.state?.data.attributes.state_id.toUpperCase()}
                  </span>{' '}
                  {t('and')}{' '}
                  <span className={styles.cityName}>
                    {props.destinationCityData?.attributes.city_name},{' '}
                    {props.destinationCityData?.attributes.state?.data.attributes.state_id.toUpperCase()}
                  </span>{' '}
                  {props.travelDate && (
                    <>
                      {t('departing on')}{' '}
                      <span className="sr-only">
                        <time dateTime={props.travelDate.toISOString()}>
                          {formatReadableDate(formatDate(props.travelDate), lng)}
                        </time>
                      </span>
                      <span aria-hidden="true" className={styles.date}>
                        <time dateTime={props.travelDate.toISOString()}>{formatDate(props.travelDate)}</time>
                      </span>
                    </>
                  )}
                  .
                </p>
                <p>
                  {t("Please check a different day's schedule for more departure dates.")}{' '}
                  {t('If you would prefer to depart from another location, please search for more terminals near you.')}
                </p>
              </>
            )}
          </>
        )}
        {props.schedules.length == 0 && props.trip === 'return' && <p>{t('No trips found')}</p>}
        {(showLoadMore ? props.schedules.slice(0, 7) : props.schedules).map(
          (schedule: ScheduleProduct, index: number) => {
            const { scheduleRun } = schedule;
            const carrier = getCarrier(schedule, props.carrierInfo);
            const carriers = getCarriers(schedule, props.carrierInfo);
            let uniqueCarriers : any[] = [];
            try {
              uniqueCarriers = Array.from(new Map(carriers.map((c) => [c.code, c])).values());
            } catch (error) {}

            const fares = getScheduleFares(schedule);
            const tabs = buildTabs(schedule);
            const remainingCapacity =
              isScheduleBookable(scheduleRun) && scheduleRun.remainingCapacity && scheduleRun.remainingCapacity <= 5;
            const departTime = formatTime(scheduleRun.departTime, scheduleRun.origin.timeZone);
            const arrivalTime = formatTime(scheduleRun.arriveTime, scheduleRun.destination.timeZone);
            const numberOfTransfers = transferNumberText(schedule);
            const handleSaverFareClick = () => {
              setModal(true);
              setFareType({ schedule });
              setFareTypeSelected('saver');
              setFareTypeId(`fare-type-saver-${props.trip === 'return' ? index + 1 : index}`);
              setScheduleRunDetails(schedule);
            };
            const handleFlexFareClick = () => {
              setModal(true);
              setFareType({ schedule });
              setFareTypeSelected('flex');
              setFareTypeId(`fare-type-flex-${props.trip === 'return' ? index + 1 : index}`);
              setScheduleRunDetails({ scheduleRun });
            };
            const strapiCarrier = props.carrierInfo[carrier?.code] || null;
            return (
              <div className={styles.schedule} key={index}>
                {!props.isSelected && (
                  <div className={styles.tabs}>
                    {isScheduleBookable(scheduleRun) &&
                      tabs.map((tab: { key: string; label: string }, index: number) => {
                        index = index + 1;
                        return (
                          <div
                            className={classNames({
                              [styles.first]: index === 1,
                              [styles.last]: index === tabs.length,
                              [styles.tab]: true,
                              [styles[tab.key]]: true
                            })}
                            key={index}
                          >
                            <span
                              className={classNames({ [styles.label]: true, [styles[tab.key]]: true })}
                              tabIndex={index === 1 ? -1 : undefined}
                              id={index === 1 && !props.isReschedule ? 'focusable-element' : undefined}
                            >
                              {tab.label}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                )}
                <div
                  className={classNames('schedule-card', {
                    [styles.card]: true,
                    [styles.active]: index === activeIndex
                  })}
                  data-depart-time={departTime}
                  data-depart-date={formatDateString(scheduleRun.departTime, scheduleRun.origin.timeZone)}
                  data-arrive-time={arrivalTime}
                  data-arrive-date={formatDateString(scheduleRun.arriveTime, scheduleRun.destination.timeZone)}
                  data-card-index={index}
                  onKeyDown={(e: any) => handleKeyDown(e, index)}
                  onClick={(e: any) => {
                    if (activeIndex !== null && !ref.current[activeIndex]?.contains(e.target)) {
                      setActiveIndex(null);
                    } else if (activeIndex === null && e.target.classList.contains('sr-only')) {
                      setActiveIndex(index);
                    }
                  }}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={0}
                  ref={(el) => {
                    ref.current[index] = el;
                  }}
                >
                  {/* When a card receives focus, it is designed to first articulate all tab names, followed by the
                  remaining seats, and then followed by the {{ origin }} to {{ destination }}...*/}
                  {!props.isSelected &&
                    isScheduleBookable(scheduleRun) &&
                    tabs.map((tab: { key: string; label: string }, index: number) => {
                      return (
                        <span className="sr-only" key={index}>
                          {tab.label}
                        </span>
                      );
                    })}
                  {remainingCapacity && (
                    <span className="sr-only">
                      {scheduleRun.remainingCapacity} {t('seats remaining for this trip')}
                    </span>
                  )}
                  <span className="sr-only">
                    {t(
                      '{{ origin }} to {{ destination }} leaving {{ departTime }} arriving {{ arrivalTime }} {{ numberOfTransfers }} from {{ saverFarePrice }}, tab to the next search result, press spacebar then use arrow keys or tab to explore or select this trip',
                      {
                        origin: scheduleRun.origin.stationName,
                        destination: scheduleRun.destination.stationName,
                        departTime: departTime,
                        arrivalTime: arrivalTime,
                        numberOfTransfers: numberOfTransfers,
                        saverFarePrice: formatPrice(fares.saver[0]?.amount)
                      }
                    )}
                  </span>
                  <div className={styles.information}>
                    <div className={styles.block}>
                      <Carrier
                        carrier={carrier}
                        carrierImages={props.carrierImages}
                        index={index}
                        strapiCarrier={strapiCarrier}
                        uniqueCarriers={uniqueCarriers}
                      />
                      <div className={styles.items}>
                        <div className={styles.times}>
                          <div className={styles.time}>
                            <span className="sr-only">{t('Departing')}</span>
                            <time dateTime={formatTime24Hour(scheduleRun.departTime)}>
                              {formatTime(scheduleRun.departTime, scheduleRun.origin.timeZone)}
                            </time>
                          </div>
                          <div className={styles['arrow-mobile']}></div>
                          <div className={styles.time}>
                            <span className="sr-only">{t('Arriving')}</span>
                            <time dateTime={formatTime24Hour(scheduleRun.arriveTime)}>
                              {formatTime(scheduleRun.arriveTime, scheduleRun.destination.timeZone)}
                            </time>
                          </div>
                        </div>
                        <div className={styles.details}>
                          <div className={styles.origin}>
                            <span className="sr-only">{t('From')}</span>
                            <StationDetails
                              cityData={props.trip === 'outbound' ? props.originCityData : props.destinationCityData}
                              stopUuid={scheduleRun.origin.stopUuid}
                            />
                          </div>

                          <div className={styles.arrow}></div>

                          <div className={styles.destination}>
                            <span className="sr-only">{t('To')}</span>
                            <StationDetails
                              cityData={props.trip === 'outbound' ? props.destinationCityData : props.originCityData}
                              stopUuid={scheduleRun.destination.stopUuid}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.bottom}>
                      <ButtonTertiary
                        classNames="gtm-schedules_trip_details"
                        dark
                        id={`trip-details-${index}`}
                        onClick={() => {
                          setModal(true);
                          setTripDetailsId(`trip-details-${index}`);
                          setTripDetails({ schedule });
                        }}
                        tabIndex={activeIndex === index ? 0 : -1}
                      >
                        <span className={styles.tertiary}>{t('Trip Details')}</span>
                      </ButtonTertiary>
                      <div className={styles.info}>
                        <div className={styles.transfers}>
                          <span className="sr-only">{t('This trip has')}</span>
                          {numberOfTransfers}
                        </div>
                        <div className={styles.line}>
                          <Image
                            alt={t('separator')}
                            fill
                            src="/assets/images/global/schedule-cards/line.svg"
                            aria-hidden="true"
                          />
                        </div>
                        <div className={styles.duration}>
                          <span className="sr-only">
                            {t('Trip Duration')} {formatDurationSROnly(scheduleRun.travelDuration, t)}
                          </span>
                          <span aria-hidden="true">{formatDuration(scheduleRun.travelDuration)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.fares}>
                    {props.fareSelected !== 'flex' && (
                      <div
                        className={classNames({
                          [styles.saver]: true,
                          [styles.spacer]: lng === 'fr' && !isMobile && !isTab
                        })}
                      >
                        <div className={styles.fare}>
                          <div className={styles.link}>
                            <ButtonTertiary
                              underline={fares.saver[0]?.amount ? true : false}
                              onClick={fares.saver[0]?.amount ? handleSaverFareClick : undefined}
                              tabIndex={-1}
                            >
                              {t('Saver Fare')}
                            </ButtonTertiary>
                            {fares.saver[0]?.amount && (
                              <ButtonTertiary
                                dark
                                id={`fare-type-saver-${props.trip === 'return' ? index + 1 : index}`}
                                onClick={handleSaverFareClick}
                                tabIndex={activeIndex === index ? 0 : -1}
                              >
                                <div aria-label={t('More information about Fare Types')} className={styles.icon}>
                                  <Image alt={t('icon')} fill src="/assets/images/global/schedule-cards/icon.svg" />
                                </div>
                              </ButtonTertiary>
                            )}
                          </div>
                          <div className={styles.price}>{formatPrice(fares.saver[0]?.amount)}</div>
                          <div className={styles.type}>{props.isSelected ? t('outbound') : t(props.trip || '')}</div>
                        </div>
                        <ButtonSmall
                          disabled={
                            !isScheduleBookable(scheduleRun) ||
                            props.isSelected ||
                            isUnavailable(fares.saver[0]?.amount, 'saver')
                          }
                          id={`upgrade-trip-${index}`}
                          onClick={() => {
                            if (
                              fares.flex.length !== 0 &&
                              !props.fareSelected &&
                              !isUnavailable(fares.flex[0]?.amount, 'flex')
                            ) {
                              setModal(true);
                              setUpgradeTrip({ scheduleRun });
                              setUpgradeTripId(`upgrade-trip-${index}`);
                              return;
                            }
                            handleOnSelect(scheduleRun, 'saver');
                          }}
                          secondary
                          classNames="gtm-schedules_buy_now selectable-fare"
                          tabIndex={activeIndex === index ? 0 : -1}
                        >
                          <span className={styles.buy}>
                            {scheduleRun.alreadyDeparted ? (
                              t('Departed')
                            ) : scheduleRun.soldOut ? (
                              t('Sold out')
                            ) : isUnavailable(fares.saver[0]?.amount, 'saver') || scheduleRun.isUnavailable ? (
                              t('Unavailable')
                            ) : props.isSelected ? (
                              t('Selected')
                            ) : (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: t("Book <span class='sr-only'>Saver Fare</span> Now")
                                }}
                              />
                            )}
                          </span>
                        </ButtonSmall>
                      </div>
                    )}
                    {props.fareSelected !== 'saver' && (
                      <div
                        className={classNames({
                          [styles.flex]: true,
                          [styles.spacer]: lng === 'fr' && !isMobile && !isTab
                        })}
                      >
                        <div className={styles.fare}>
                          <div className={styles.link}>
                            <ButtonTertiary
                              underline={fares.flex[0]?.amount ? true : false}
                              onClick={fares.flex[0]?.amount ? handleFlexFareClick : undefined}
                              tabIndex={-1}
                            >
                              {t('Flex Fare')}
                            </ButtonTertiary>
                            {fares.flex[0]?.amount && (
                              <ButtonTertiary
                                dark
                                id={`fare-type-flex-${props.trip === 'return' ? index + 1 : index}`}
                                onClick={handleFlexFareClick}
                                tabIndex={activeIndex === index ? 0 : -1}
                              >
                                <div aria-label={t('More information about Fare Types')} className={styles.icon}>
                                  <Image alt={t('icon')} fill src="/assets/images/global/schedule-cards/icon.svg" />
                                </div>
                              </ButtonTertiary>
                            )}
                          </div>
                          <div className={styles.price}>{formatPrice(fares.flex[0]?.amount)}</div>
                          <div className={styles.type}>{props.isSelected ? t('outbound') : t(props.trip || '')}</div>
                        </div>
                        <ButtonSmall
                          disabled={
                            !isScheduleBookable(scheduleRun) ||
                            props.isSelected ||
                            isUnavailable(fares.flex[0]?.amount, 'flex')
                          }
                          onClick={() => {
                            handleOnSelect(scheduleRun, 'flex');
                          }}
                          primary
                          classNames="gtm-schedules_buy_now selectable-fare"
                          tabIndex={activeIndex === index ? 0 : -1}
                        >
                          <span className={styles.buy}>
                            {scheduleRun.alreadyDeparted ? (
                              t('Departed')
                            ) : scheduleRun.soldOut ? (
                              t('Sold out')
                            ) : isUnavailable(fares.flex[0]?.amount, 'flex') || scheduleRun.isUnavailable ? (
                              t('Unavailable')
                            ) : props.isSelected ? (
                              t('Selected')
                            ) : (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: t("Book <span class='sr-only'>Flex Fare</span> Now")
                                }}
                              />
                            )}
                          </span>
                        </ButtonSmall>
                      </div>
                    )}
                  </div>
                </div>
                {remainingCapacity && (
                  <div aria-describedby={`seats-remaining-${scheduleRun.scheduleUuid}`} className={styles.remaining}>
                    <p className={styles.text} id={`seats-remaining-${scheduleRun.scheduleUuid}`}>
                      {scheduleRun.remainingCapacity} {t('seats remaining for this trip')}
                    </p>
                  </div>
                )}
              </div>
            );
          }
        )}
        <Modal title={t('Trip Details')} isOpen={tripDetails !== null} onClose={onCloseTripDetails}>
          <ScheduleTripDetails
            tripDetails={tripDetails && tripDetails.schedule}
            onClose={onCloseTripDetails}
            carrierImages={props.carrierImages}
            carrierInfo={props.carrierInfo}
          />
        </Modal>
        <Modal title={t('Select Fare')} isOpen={fareType !== null} onClose={onCloseFareTypeSelection}>
          <ScheduleFareTypeSelection
            fares={fareType?.schedule ? getScheduleFares(fareType.schedule) : null}
            fareSelected={props.fareSelected}
            fareType={fareType && fareType.schedule}
            fareTypeSelected={fareTypeSelected}
            isReschedule={props.isReschedule}
            onBuyFlexFare={onBuyFlexFare}
            onBuySaverFare={onBuySaverFare}
            onClose={onCloseFareTypeSelection}
          />
        </Modal>
        <Modal title={t('Upgrade Trip')} isOpen={upgradeTrip !== null} onClose={onCloseUpgradeTrip} size="small">
          <ScheduleUpgradeTrip
            onClose={onCloseUpgradeTrip}
            onSelect={(fareType: string) => {
              if (upgradeTrip && upgradeTrip.scheduleRun) {
                handleOnSelect(upgradeTrip.scheduleRun, fareType);
              }
            }}
          />
        </Modal>
      </div>
      {showLoadMore && !props.isSelected && (
        <div className={styles.more}>
          <div>
            <span className={styles.text}>{t('Showing')} </span>
            <span className={styles.count}>{props.schedules.length > 7 ? 7 : props.schedules.length}</span>
            <span className={styles.text}> {t('trips')} </span>
            <span className={styles.text}> {t('out of')} </span>
            <span className={styles.count}>{props.schedules.length}</span>
          </div>
          <Button onClick={() => setShowLoadMore(false)} primary classNames="gtm-schedules_load_more">
            <span className={styles.button}>{t('Load More')}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScheduleCards;
